import { type RefObject, useLayoutEffect, useRef } from 'react'
import { computeScrollProgress } from '../lib/scrub.js'

const FALLBACK_VIEWPORT_HEIGHT = 800
const SCRUB_TRACK_VIEWPORTS = 4
const ORIENTATION_SETTLE_MS = 150
const VIEWPORT_HEIGHT_PROPERTY = '--scrub-viewport-height'
const TRACK_HEIGHT_PROPERTY = '--scrub-track-height'

/**
 * rAF 루프 하나로 progress를 내보낸다.
 * 매 프레임 setState 하면 리렌더 폭풍이 난다 — 소비자가 ref로 DOM을 직접 갱신한다.
 *
 * 루프는 섹션이 뷰포트 근처에 있을 때만 돈다. 예전에는 두 필름의 루프가 세션 내내
 * 매 프레임 getBoundingClientRect()를 불러 — 하객이 두 필름에서 한참 떨어진 본문을
 * 읽는 동안에도 — 프레임마다 강제 레이아웃을 두 번씩 일으켰다. 컬링은 콜백만
 * 건너뛸 뿐 측정 자체는 막지 못했다.
 *
 * 관찰자가 없거나 아직 아무 말도 하지 않았으면 루프는 돈다. 실패는 늘 "동작하되
 * 조금 비싼" 쪽으로 떨어져야 한다 — 조용히 멈춘 스크럽은 고장으로 보인다.
 */
export function useScrollScrub(
  ref: RefObject<HTMLElement | null>,
  onProgress: (progress: number) => void,
  active = true,
): void {
  const callback = useRef(onProgress)
  callback.current = onProgress

  useLayoutEffect(() => {
    if (!active) return
    let raf = 0
    let running = false
    let resizeTimer = 0
    let stableViewportWidth = window.innerWidth
    let stableViewportHeight =
      Number.isFinite(window.innerHeight) && window.innerHeight > 0
        ? window.innerHeight
        : FALLBACK_VIEWPORT_HEIGHT
    let styledElement: HTMLElement | null = null

    const applyStableViewport = (element: HTMLElement) => {
      if (styledElement !== null && styledElement !== element) {
        styledElement.style.removeProperty(VIEWPORT_HEIGHT_PROPERTY)
        styledElement.style.removeProperty(TRACK_HEIGHT_PROPERTY)
      }
      styledElement = element
      element.style.setProperty(VIEWPORT_HEIGHT_PROPERTY, `${stableViewportHeight}px`)
      element.style.setProperty(
        TRACK_HEIGHT_PROPERTY,
        `${stableViewportHeight * SCRUB_TRACK_VIEWPORTS}px`,
      )
    }

    const initialElement = ref.current
    if (initialElement !== null) applyStableViewport(initialElement)

    const tick = () => {
      raf = requestAnimationFrame(tick)
      const element = ref.current
      if (element === null) return
      if (element !== styledElement) applyStableViewport(element)
      const rect = element.getBoundingClientRect()
      // 트랙 높이는 세션 고정값이지만 영상 화면은 현재 뷰포트를 채운다. 진행률도
      // 현재 화면 높이를 빼야 sticky가 끝나는 지점과 마지막 프레임이 일치한다.
      // 주소창이 접혀 height가 커져도 고정 트랙의 travel은 줄어들므로 역행하지 않는다.
      // dvh와 layout viewport(innerHeight)가 다른 Safari/WebView가 있으므로 실제
      // sticky의 렌더 높이를 우선한다. 테스트처럼 자식이 없는 소비자는 기존
      // window 높이로 안전하게 물러난다.
      const renderedViewportHeight = element.firstElementChild?.clientHeight ?? 0
      const currentViewportHeight = renderedViewportHeight > 0
        ? renderedViewportHeight
        : Number.isFinite(window.innerHeight) && window.innerHeight > 0
          ? window.innerHeight
          : stableViewportHeight
      // 화면 밖이면 계산만 하고 그리기는 소비자가 건너뛴다.
      if (rect.bottom < -60 || rect.top > currentViewportHeight + 60) return
      callback.current(computeScrollProgress(rect.top, rect.height, currentViewportHeight))
    }

    const start = () => {
      if (running) return
      running = true
      raf = requestAnimationFrame(tick)
    }
    const stop = () => {
      if (!running) return
      running = false
      cancelAnimationFrame(raf)
    }

    const handleResize = () => {
      // 모바일 주소창 show/hide는 보통 높이만 바꾼다. 그 변화로 스크럽 범위를
      // 다시 잡으면 사용자가 위로 밀어도 영상이 뒤로 간다. 폭이 바뀌는 실제
      // 회전/창 리사이즈만 마지막 resize가 가라앉은 뒤 새 기준으로 채택한다.
      window.clearTimeout(resizeTimer)
      resizeTimer = 0
      if (window.innerWidth === stableViewportWidth) return
      resizeTimer = window.setTimeout(() => {
        resizeTimer = 0
        if (window.innerWidth === stableViewportWidth) return
        stableViewportWidth = window.innerWidth
        stableViewportHeight =
          Number.isFinite(window.innerHeight) && window.innerHeight > 0
            ? window.innerHeight
            : FALLBACK_VIEWPORT_HEIGHT
        const element = ref.current
        if (element !== null) applyStableViewport(element)
      }, ORIENTATION_SETTLE_MS)
    }

    window.addEventListener('resize', handleResize)

    start()

    const element = ref.current
    let observer: IntersectionObserver | null = null
    if (element !== null && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (entry === undefined) return
          if (entry.isIntersecting) start()
          else stop()
        },
        // 화면에 걸리기 전부터 돌려 둔다 — 스크럽이 뒤늦게 깨어나 튀지 않게.
        { rootMargin: '200px 0px' },
      )
      observer.observe(element)
    }

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', handleResize)
      window.clearTimeout(resizeTimer)
      stop()
      styledElement?.style.removeProperty(VIEWPORT_HEIGHT_PROPERTY)
      styledElement?.style.removeProperty(TRACK_HEIGHT_PROPERTY)
    }
  }, [ref, active])
}
